import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = "K&K Medical and Dental";
const CANONICAL_ORIGIN = "https://www.kkmedical.co.za";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/y9kopzrq_Black%20Elegant%20Modern%20Name%20Initials%20Monogram%20Logo.png";

// Normalize a pathname so /about, /about/, /about?x=1 all canonicalize identically.
const normalizePath = (pathname) => {
  if (!pathname) return "/";
  const clean = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  return clean;
};

// Directly write/update a single element in <head> (bypasses Helmet quirks for critical SEO tags).
const setHeadTag = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    el.setAttribute(k, v);
  });
};

export const SEO = ({
  title,
  description,
  keywords = "",
  image = DEFAULT_IMAGE,
  canonicalUrl,
  noIndex = false,
  type = "website"
}) => {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Affordable Healthcare in Umzinto & Scottburgh, KZN`;
  const canonical = canonicalUrl || `${CANONICAL_ORIGIN}${normalizePath(location.pathname)}`;
  const robotsContent = noIndex ? "noindex, nofollow" : "index, follow";

  // Directly control the critical SEO head tags every time these values change.
  useEffect(() => {
    document.title = fullTitle;
    setHeadTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
    setHeadTag('meta[name="robots"]', 'meta', { name: 'robots', content: robotsContent });
    if (description) {
      setHeadTag('meta[name="description"]', 'meta', { name: 'description', content: description });
    }
    setHeadTag('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonical });
    setHeadTag('meta[property="og:title"]', 'meta', { property: 'og:title', content: fullTitle });
    if (description) {
      setHeadTag('meta[property="og:description"]', 'meta', { property: 'og:description', content: description });
    }
  }, [fullTitle, canonical, robotsContent, description]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};
